/**
 * AdminAffiliates.jsx
 * Affiliate System — Admin UI
 * Embedded as a tab inside AdminDashboard
 * Light theme + balanced desktop layout
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;

const PLATFORMS = ["TikTok", "Instagram", "X", "YouTube", "Other"];

const MONTH_NAMES = ["يناير","فبراير","مارس","أبريل","مايو","يونيو",
                     "يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];

function statusBadge(s) {
  return s === "active"
    ? <span style={{ background: "#dcfce7", color: "#16a34a", fontWeight: 700, fontSize: "0.72rem", padding: "2px 9px", borderRadius: "9999px" }}>فعّال</span>
    : <span style={{ background: "#fee2e2", color: "#dc2626", fontWeight: 700, fontSize: "0.72rem", padding: "2px 9px", borderRadius: "9999px" }}>معطّل</span>;
}

function commBadge(s) {
  const map = {
    payable:  { bg: "#fef3c7", color: "#92400e", label: "مستحق" },
    paid:     { bg: "#dcfce7", color: "#16a34a", label: "مدفوع" },
    reversed: { bg: "#fee2e2", color: "#dc2626", label: "معكوس" },
    pending:  { bg: "#f3f4f6", color: "#6b7280", label: "معلّق" },
  };
  const m = map[s] || { bg: "#f3f4f6", color: "#6b7280", label: s };
  return (
    <span style={{ background: m.bg, color: m.color, fontWeight: 700, fontSize: "0.72rem", padding: "2px 9px", borderRadius: "9999px" }}>
      {m.label}
    </span>
  );
}

export default function AdminAffiliates({ token }) {
  const now = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data,  setData]  = useState(null);
  const [loadingList, setLoadingList] = useState(false);

  const [view,   setView]   = useState("list");
  const [selAff, setSelAff] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/affiliates`, {
        params: { year, month }, headers
      });
      setData(d);
    } catch { toast.error("تعذّر تحميل الشركاء"); }
    finally { setLoadingList(false); }
  }, [year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (view === "list") loadList(); }, [view, loadList]);

  // ── Month selector ──────────────────────────────────────────────────────────
  const MonthSelector = () => {
    const months = [];
    const cur = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(cur.getFullYear(), cur.getMonth() - i, 1);
      months.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
    }
    return (
      <select
        value={`${year}-${month}`}
        onChange={e => { const [y,m] = e.target.value.split("-"); setYear(+y); setMonth(+m); }}
        style={selectStyle}
      >
        {months.map(({ year: y, month: m }) => (
          <option key={`${y}-${m}`} value={`${y}-${m}`}>
            {MONTH_NAMES[m - 1]} {y}
          </option>
        ))}
      </select>
    );
  };

  // ── Create Form ─────────────────────────────────────────────────────────────
  const CreateForm = () => {
    const [form, setForm] = useState({
      display_name: "", discount_code: "", social_platform: "",
      social_username: "", social_url: "",
      customer_discount_percent: 5, commission_percent: 7.5, note: ""
    });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
      if (!form.display_name.trim() || !form.discount_code.trim()) {
        toast.error("الاسم والكود مطلوبان"); return;
      }
      setSaving(true);
      try {
        await axios.post(`${API}/admin/affiliates`, form, { headers });
        toast.success("تم إنشاء الشريك");
        setView("list");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "خطأ في الحفظ");
      } finally { setSaving(false); }
    };

    return (
      <div style={formCard}>
        <h3 style={formTitleStyle}>➕ شريك جديد</h3>

        {/* Row 1: Name + Code */}
        <div style={twoCol}>
          <Field label="الاسم *" value={form.display_name}
            onChange={v => setForm({...form, display_name: v})} placeholder="مجيد العنزي" />
          <Field label="الكود *" value={form.discount_code}
            onChange={v => setForm({...form, discount_code: v.toUpperCase().replace(/[^A-Z0-9_]/g,"")})}
            placeholder="MAJEED" dir="ltr" />
        </div>

        {/* Row 2: Discount % + Commission % */}
        <div style={twoCol}>
          <FieldNum label="نسبة خصم العميل %" value={form.customer_discount_percent}
            onChange={v => setForm({...form, customer_discount_percent: v})} />
          <FieldNum label="نسبة العمولة %" value={form.commission_percent}
            onChange={v => setForm({...form, commission_percent: v})} />
        </div>

        {/* Row 3: Platform + Username */}
        <div style={twoCol}>
          <div>
            <label style={labelStyle}>المنصة الاجتماعية</label>
            <select value={form.social_platform}
              onChange={e => setForm({...form, social_platform: e.target.value})} style={inputStyle}>
              <option value="">— اختياري —</option>
              {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <Field label="اسم المستخدم" value={form.social_username}
            onChange={v => setForm({...form, social_username: v})} placeholder="@majeed" dir="ltr" />
        </div>

        {/* Row 4: URL full width */}
        <Field label="رابط الملف الشخصي" value={form.social_url}
          onChange={v => setForm({...form, social_url: v})} placeholder="https://..." dir="ltr" />

        {/* Row 5: Note full width */}
        <Field label="ملاحظة داخلية" value={form.note}
          onChange={v => setForm({...form, note: v})} placeholder="اختياري" />

        <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "flex-end" }}>
          <button onClick={() => setView("list")} style={btnGhost}>إلغاء</button>
          <button onClick={handleSave} disabled={saving} style={btnPrimary}>
            {saving ? "..." : "حفظ الشريك"}
          </button>
        </div>
      </div>
    );
  };

  // ── Edit Form ───────────────────────────────────────────────────────────────
  const EditForm = ({ aff, onDone }) => {
    const [form, setForm] = useState({
      display_name:              aff.display_name || "",
      social_platform:           aff.social_platform || "",
      social_username:           aff.social_username || "",
      social_url:                aff.social_url || "",
      customer_discount_percent: aff.customer_discount_percent,
      commission_percent:        aff.commission_percent,
      status:                    aff.status,
      note:                      aff.note || "",
    });
    const [saving, setSaving]  = useState(false);
    const [confirmed, setConf] = useState(false);
    const ratesChanged =
      form.customer_discount_percent !== aff.customer_discount_percent ||
      form.commission_percent        !== aff.commission_percent;

    const handleSave = async () => {
      if (ratesChanged && !confirmed) { setConf(true); return; }
      setSaving(true);
      try {
        await axios.put(`${API}/admin/affiliates/${aff.id}`, form, { headers });
        toast.success("تم التعديل");
        onDone();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "خطأ في التعديل");
      } finally { setSaving(false); setConf(false); }
    };

    return (
      <div>
        <h3 style={formTitleStyle}>✏️ تعديل — {aff.discount_code}</h3>

        {confirmed && ratesChanged && (
          <div style={{ background: "#fef3c7", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 10, padding: "12px 16px", marginBottom: 16, fontSize: "0.82rem", color: "#92400e" }}>
            ⚠️ سيتم تطبيق النسب الجديدة على المشتريات الجديدة فقط.
          </div>
        )}

        <div style={{ display: "grid", gap: 16 }}>
          {/* Row 1: Name */}
          <Field label="الاسم" value={form.display_name} onChange={v => setForm({...form, display_name: v})} />

          {/* Row 2: Rates */}
          <div style={twoCol}>
            <FieldNum label="نسبة خصم العميل %" value={form.customer_discount_percent}
              onChange={v => setForm({...form, customer_discount_percent: v})} />
            <FieldNum label="نسبة العمولة %" value={form.commission_percent}
              onChange={v => setForm({...form, commission_percent: v})} />
          </div>

          {/* Row 3: Platform + Username */}
          <div style={twoCol}>
            <div>
              <label style={labelStyle}>المنصة</label>
              <select value={form.social_platform || ""} onChange={e => setForm({...form, social_platform: e.target.value})} style={inputStyle}>
                <option value="">—</option>
                {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <Field label="اسم المستخدم" value={form.social_username || ""} onChange={v => setForm({...form, social_username: v})} dir="ltr" />
          </div>

          {/* Row 4: URL */}
          <Field label="رابط الملف الشخصي" value={form.social_url || ""} onChange={v => setForm({...form, social_url: v})} dir="ltr" />

          {/* Row 5: Status */}
          <div>
            <label style={labelStyle}>الحالة</label>
            <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} style={inputStyle}>
              <option value="active">فعّال</option>
              <option value="inactive">معطّل</option>
            </select>
          </div>

          {/* Row 6: Note */}
          <Field label="ملاحظة داخلية" value={form.note || ""} onChange={v => setForm({...form, note: v})} />
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "flex-end" }}>
          <button onClick={onDone} style={btnGhost}>إلغاء</button>
          <button onClick={handleSave} disabled={saving} style={btnPrimary}>
            {saving ? "..." : confirmed && ratesChanged ? "تأكيد الحفظ" : "حفظ"}
          </button>
        </div>
      </div>
    );
  };

  // ── Detail View ─────────────────────────────────────────────────────────────
  const DetailView = ({ aff }) => {
    const [detYear,  setDetYear]  = useState(year);
    const [detMonth, setDetMonth] = useState(month);
    const [detail, setDetail]     = useState(null);
    const [page, setPage]         = useState(1);
    const [loading, setLoading]   = useState(false);
    const [settling, setSettling] = useState(false);
    const [confirmSettle, setConfSettle] = useState(false);
    const [editMode, setEditMode] = useState(false);

    const loadDetail = useCallback(async () => {
      setLoading(true);
      try {
        const { data: d } = await axios.get(`${API}/admin/affiliates/${aff.id}`, {
          params: { year: detYear, month: detMonth, page, page_size: 20 }, headers
        });
        setDetail(d);
      } catch { toast.error("تعذّر تحميل التفاصيل"); }
      finally { setLoading(false); }
    }, [aff.id, detYear, detMonth, page]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadDetail(); }, [loadDetail]);

    const handleSettle = async () => {
      setSettling(true);
      try {
        const { data: res } = await axios.post(
          `${API}/admin/affiliates/${aff.id}/settle`,
          { year: detYear, month: detMonth }, { headers }
        );
        toast.success(`تمت التسوية — ${res.settled_count} عملية`);
        setConfSettle(false);
        loadDetail();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "خطأ في التسوية");
      } finally { setSettling(false); }
    };

    const handleReverse = async (convId) => {
      if (!window.confirm("عكس هذه العمولة؟")) return;
      try {
        await axios.post(`${API}/admin/affiliates/conversions/${convId}/reverse`, {}, { headers });
        toast.success("تم العكس");
        loadDetail();
      } catch { toast.error("خطأ في العكس"); }
    };

    if (editMode) return (
      <div style={formCard}>
        <EditForm aff={aff} onDone={() => { setEditMode(false); loadDetail(); loadList(); }} />
      </div>
    );

    const s = detail?.month_summary || {};
    const payable = s.commission_payable || 0;

    return (
      <div>
        {/* Header row */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          <button onClick={() => setView("list")} style={{ ...btnGhost, padding: "6px 14px", fontSize: "0.8rem" }}>← رجوع</button>
          <div style={{ flex: 1 }}>
            <h3 style={{ color: "#5B0E14", fontWeight: 900, margin: 0, fontSize: "1.2rem" }}>
              {aff.display_name}
            </h3>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4, fontSize: "0.78rem", color: "rgba(91,14,20,0.5)" }}>
              <code style={{ background: "rgba(91,14,20,0.08)", color: "#5B0E14", padding: "2px 8px", borderRadius: 6, fontFamily: "monospace" }}>
                {aff.discount_code}
              </code>
              {aff.social_platform && aff.social_username && (
                aff.social_url
                  ? <a href={aff.social_url} target="_blank" rel="noopener noreferrer" style={{ color: "#5B0E14", opacity: 0.6 }}>
                      {aff.social_platform}: @{aff.social_username}
                    </a>
                  : <span>{aff.social_platform}: @{aff.social_username}</span>
              )}
              <span>{aff.customer_discount_percent}% خصم · {aff.commission_percent}% عمولة</span>
              {statusBadge(aff.status)}
            </div>
          </div>
          <button onClick={() => setEditMode(true)} style={{ ...btnGhost, padding: "6px 14px", fontSize: "0.8rem" }}>تعديل</button>
        </div>

        {/* Month selector */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <span style={{ color: "rgba(91,14,20,0.5)", fontSize: "0.8rem", fontWeight: 600 }}>الفترة:</span>
          <select value={`${detYear}-${detMonth}`}
            onChange={e => { const [y,m]=e.target.value.split("-"); setDetYear(+y); setDetMonth(+m); setPage(1); }}
            style={{ ...selectStyle, padding: "5px 10px", fontSize: "0.8rem" }}>
            {Array.from({length:12},(_,i)=>{
              const d = new Date(new Date().getFullYear(), new Date().getMonth()-i, 1);
              return {y:d.getFullYear(),m:d.getMonth()+1};
            }).map(({y,m})=>(
              <option key={`${y}-${m}`} value={`${y}-${m}`}>{MONTH_NAMES[m-1]} {y}</option>
            ))}
          </select>
        </div>

        {loading
          ? <p style={{ color: "rgba(91,14,20,0.4)", fontSize: "0.85rem" }}>جاري التحميل...</p>
          : detail && (
          <>
            {/* Monthly summary cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px,1fr))", gap: 10, marginBottom: 20 }}>
              {[
                { label: "مبيعات ناجحة",   value: s.successful_count || 0,                  unit: "" },
                { label: "إجمالي مدفوع",   value: (s.total_paid || 0).toFixed(2),           unit: "ر.س" },
                { label: "خصومات العملاء", value: (s.total_discounts || 0).toFixed(2),      unit: "ر.س" },
                { label: "عمولة مولّدة",   value: (s.commission_generated || 0).toFixed(2), unit: "ر.س" },
                { label: "عمولة معكوسة",   value: (s.commission_reversed || 0).toFixed(2),  unit: "ر.س" },
                { label: "مدفوع للشريك",   value: (s.commission_paid || 0).toFixed(2),      unit: "ر.س" },
                { label: "مستحق الآن",     value: payable.toFixed(2),                       unit: "ر.س", highlight: payable > 0 },
              ].map(({ label, value, unit, highlight }) => (
                <div key={label} style={{
                  background: highlight ? "#fef3c7" : "#fff",
                  border: highlight ? "1px solid rgba(245,158,11,0.35)" : "1px solid rgba(91,14,20,0.08)",
                  borderRadius: 12, padding: "12px 14px",
                }}>
                  <p style={{ color: "#9ca3af", fontSize: "0.68rem", margin: "0 0 4px", fontWeight: 700 }}>{label}</p>
                  <p style={{ color: highlight ? "#92400e" : "#5B0E14", fontWeight: 900, fontSize: "1rem", margin: 0 }}>
                    {value} <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "rgba(91,14,20,0.4)" }}>{unit}</span>
                  </p>
                </div>
              ))}
            </div>

            {/* Settlement */}
            {payable > 0 && (
              <div style={{ marginBottom: 20 }}>
                {!confirmSettle ? (
                  <button onClick={() => setConfSettle(true)} style={{ ...btnPrimary, fontSize: "0.85rem", padding: "8px 20px" }}>
                    تسوية الشهر — {MONTH_NAMES[detMonth-1]} {detYear}
                  </button>
                ) : (
                  <div style={{ background: "#fef3c7", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 12, padding: "14px 18px" }}>
                    <p style={{ color: "#5B0E14", fontWeight: 800, margin: "0 0 8px", fontSize: "0.9rem" }}>
                      تأكيد تسوية: {MONTH_NAMES[detMonth-1]} {detYear}
                    </p>
                    <p style={{ color: "rgba(91,14,20,0.6)", fontSize: "0.8rem", margin: "0 0 12px" }}>
                      المبلغ المستحق: <strong style={{ color: "#92400e" }}>{payable.toFixed(2)} ر.س</strong>
                    </p>
                    <div style={{ display: "flex", gap: 10 }}>
                      <button onClick={() => setConfSettle(false)} style={btnGhost}>إلغاء</button>
                      <button onClick={handleSettle} disabled={settling} style={btnPrimary}>
                        {settling ? "..." : "✓ تم الدفع"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Conversions table */}
            <h4 style={{ color: "rgba(91,14,20,0.6)", fontWeight: 700, fontSize: "0.85rem", marginBottom: 10 }}>
              العمليات ({detail.total})
            </h4>
            <div style={{ overflowX: "auto", background: "#fff", borderRadius: 12, border: "1px solid rgba(91,14,20,0.08)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(91,14,20,0.08)" }}>
                    {["العميل","الباقة","التاريخ","الأصلي","الخصم","المدفوع","العمولة","الحالة",""].map(h => (
                      <th key={h} style={{ padding: "10px 12px", textAlign: "right", color: "#9ca3af", fontWeight: 700, background: "#f9fafb" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detail.conversions.map(c => (
                    <tr key={c.id} style={{ borderBottom: "1px solid rgba(91,14,20,0.06)" }}>
                      <td style={{ padding: "9px 12px", color: "#1a1a1a", fontWeight: 600 }}>
                        {c.customer_username || c.customer_email || "—"}
                      </td>
                      <td style={{ padding: "9px 12px", color: "rgba(91,14,20,0.65)" }}>{c.plan_name}</td>
                      <td style={{ padding: "9px 12px", color: "#9ca3af", direction: "ltr", whiteSpace: "nowrap" }}>
                        {new Date(c.created_at).toLocaleDateString("ar-SA")}
                      </td>
                      <td style={{ padding: "9px 12px", color: "rgba(91,14,20,0.65)" }}>{c.original_price?.toFixed(2)}</td>
                      <td style={{ padding: "9px 12px", color: "#16a34a", fontWeight: 700 }}>-{c.discount_amount?.toFixed(2)}</td>
                      <td style={{ padding: "9px 12px", color: "#5B0E14", fontWeight: 800 }}>{c.final_customer_price?.toFixed(2)}</td>
                      <td style={{ padding: "9px 12px", color: "#92400e", fontWeight: 700 }}>{c.commission_amount?.toFixed(2)}</td>
                      <td style={{ padding: "9px 12px" }}>{commBadge(c.commission_status)}</td>
                      <td style={{ padding: "9px 12px" }}>
                        {c.commission_status === "payable" && (
                          <button onClick={() => handleReverse(c.id)}
                            style={{ background: "#fee2e2", border: "none", color: "#dc2626", borderRadius: 6, padding: "3px 10px", fontSize: "0.7rem", cursor: "pointer", fontWeight: 700 }}>
                            عكس
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {detail.conversions.length === 0 && (
                    <tr>
                      <td colSpan={9} style={{ padding: "24px", textAlign: "center", color: "rgba(91,14,20,0.3)", fontSize: "0.8rem" }}>
                        لا توجد عمليات في هذه الفترة
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {detail.total > 20 && (
              <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 16 }}>
                <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1} style={btnGhost}>السابق</button>
                <span style={{ color: "#9ca3af", fontSize: "0.8rem", alignSelf: "center" }}>
                  {page} / {Math.ceil(detail.total/20)}
                </span>
                <button onClick={() => setPage(p => p+1)} disabled={page*20 >= detail.total} style={btnGhost}>التالي</button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // ── Routing ─────────────────────────────────────────────────────────────────
  if (view === "create") return (
    <div style={pageShell}>
      <div style={pageInner}>
        <CreateForm />
      </div>
    </div>
  );

  if (view === "detail" && selAff) return (
    <div style={pageShell}>
      <div style={pageInner}>
        <DetailView aff={selAff} />
      </div>
    </div>
  );

  // ── List View ───────────────────────────────────────────────────────────────
  return (
    <div style={pageShell}>
      <div style={pageInner}>

        {/* Page header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
          <h2 style={{ color: "#5B0E14", fontWeight: 900, margin: 0, fontSize: "1.3rem" }}>🤝 نظام الشركاء</h2>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <MonthSelector />
            <button onClick={() => setView("create")} style={{ ...btnPrimary, fontSize: "0.85rem", padding: "8px 18px" }}>
              + شريك جديد
            </button>
          </div>
        </div>

        {/* Dashboard totals */}
        {data?.totals && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px,1fr))", gap: 12, marginBottom: 28 }}>
            {[
              { label: "شركاء فعّالون",  value: data.totals.active_count },
              { label: "مبيعات الشهر",   value: data.totals.total_sales },
              { label: "إيراد الأكواد",  value: (data.totals.total_revenue?.toFixed(2) ?? "0.00") + " ر.س" },
              { label: "عمولات مستحقة", value: (data.totals.outstanding?.toFixed(2)   ?? "0.00") + " ر.س", highlight: data.totals.outstanding > 0 },
            ].map(({ label, value, highlight }) => (
              <div key={label} style={{
                background: highlight ? "#fef3c7" : "#fff",
                border: highlight ? "1px solid rgba(245,158,11,0.35)" : "1px solid rgba(91,14,20,0.08)",
                borderRadius: 14, padding: "16px 18px",
              }}>
                <p style={{ color: "#9ca3af", fontSize: "0.72rem", fontWeight: 700, margin: "0 0 6px" }}>{label}</p>
                <p style={{ color: highlight ? "#92400e" : "#5B0E14", fontWeight: 900, fontSize: "1.2rem", margin: 0 }}>{value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Affiliate list */}
        {loadingList ? (
          <p style={{ color: "rgba(91,14,20,0.4)", textAlign: "center", padding: 40 }}>جاري التحميل...</p>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {(data?.affiliates || []).map(aff => {
              const s = aff.month_stats || {};
              return (
                <div key={aff.id} style={{
                  background: "#fff",
                  border: "1px solid rgba(91,14,20,0.1)",
                  borderRadius: 14, padding: "16px 20px",
                  display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap",
                }}>
                  {/* Main info */}
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                      <span style={{ color: "#5B0E14", fontWeight: 900, fontSize: "1rem" }}>{aff.display_name}</span>
                      <code style={{ background: "rgba(91,14,20,0.08)", color: "#5B0E14", padding: "2px 8px", borderRadius: 6, fontSize: "0.75rem", fontFamily: "monospace" }}>
                        {aff.discount_code}
                      </code>
                      {statusBadge(aff.status)}
                    </div>
                    {aff.social_platform && aff.social_username && (
                      <div style={{ fontSize: "0.75rem", color: "rgba(91,14,20,0.45)", marginBottom: 4 }}>
                        {aff.social_url
                          ? <a href={aff.social_url} target="_blank" rel="noopener noreferrer" style={{ color: "#5B0E14", opacity: 0.55 }}>
                              {aff.social_platform}: @{aff.social_username}
                            </a>
                          : <span>{aff.social_platform}: @{aff.social_username}</span>
                        }
                      </div>
                    )}
                    <div style={{ fontSize: "0.76rem", color: "rgba(91,14,20,0.5)" }}>
                      {aff.customer_discount_percent}% خصم · {aff.commission_percent}% عمولة
                    </div>
                  </div>

                  {/* Stats */}
                  <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: "0.8rem" }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ color: "#9ca3af", fontSize: "0.68rem", fontWeight: 700, marginBottom: 2 }}>عمليات الشهر</div>
                      <div style={{ color: "#5B0E14", fontWeight: 900, fontSize: "1rem" }}>{s.successful_count || 0}</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ color: "#9ca3af", fontSize: "0.68rem", fontWeight: 700, marginBottom: 2 }}>إجمالي مدفوع</div>
                      <div style={{ color: "#5B0E14", fontWeight: 900, fontSize: "1rem" }}>{(s.total_paid||0).toFixed(2)} <span style={{ fontSize: "0.7rem" }}>ر.س</span></div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ color: "#9ca3af", fontSize: "0.68rem", fontWeight: 700, marginBottom: 2 }}>مستحق</div>
                      <div style={{ color: s.commission_payable > 0 ? "#92400e" : "#5B0E14", fontWeight: 900, fontSize: "1rem" }}>
                        {(s.commission_payable||0).toFixed(2)} <span style={{ fontSize: "0.7rem" }}>ر.س</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => { setSelAff(aff); setView("detail"); }}
                      style={{ ...btnGhost, fontSize: "0.78rem", padding: "6px 14px" }}>تفاصيل</button>
                    <button onClick={() => { setSelAff(aff); setView("edit"); }}
                      style={{ ...btnGhost, fontSize: "0.78rem", padding: "6px 14px" }}>تعديل</button>
                  </div>
                </div>
              );
            })}
            {(data?.affiliates || []).length === 0 && (
              <div style={{ textAlign: "center", padding: 56, color: "rgba(91,14,20,0.3)", fontSize: "0.95rem" }}>
                لا يوجد شركاء بعد — أضف أول شريك
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit modal overlay */}
      {view === "edit" && selAff && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20 }}>
          <div style={{ background: "#fff", border: "1px solid rgba(91,14,20,0.15)", borderRadius: 20, padding: 28, maxWidth: 680, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
            <EditForm aff={selAff} onDone={() => { setView("list"); loadList(); }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────
function Field({ label, value, onChange, placeholder, dir }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input type="text" value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} dir={dir}
        style={{ ...inputStyle, direction: dir || "rtl" }} />
    </div>
  );
}
function FieldNum({ label, value, onChange }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input type="number" step="0.1" min="0" max="100" value={value}
        onChange={e => onChange(parseFloat(e.target.value) || 0)} style={inputStyle} />
    </div>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────────
// pageShell: full-width tab wrapper with admin-standard padding
const pageShell = {
  padding: "24px",
  minHeight: "60vh",
};

// pageInner: centered content container — max 1100px, centered
const pageInner = {
  maxWidth: 1100,
  margin: "0 auto",
};

// formCard: white card for create/edit forms
const formCard = {
  background: "#fff",
  border: "1px solid rgba(91,14,20,0.1)",
  borderRadius: 16,
  padding: "28px 32px",
  maxWidth: 800,
  margin: "0 auto",
};

// twoCol: 2-column responsive grid
const twoCol = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: 16,
};

// ── Shared styles ─────────────────────────────────────────────────────────────
const labelStyle = {
  display: "block",
  color: "rgba(0,0,0,0.45)",
  fontSize: "0.75rem",
  fontWeight: 700,
  marginBottom: 6,
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  background: "#fff",
  border: "1.5px solid #e5e7eb",
  color: "#1a1a1a",
  borderRadius: 10,
  padding: "9px 12px",
  fontSize: "0.85rem",
  fontFamily: "Cairo, sans-serif",
  outline: "none",
};

const selectStyle = {
  background: "#fff",
  border: "1.5px solid #e5e7eb",
  color: "#5B0E14",
  borderRadius: 10,
  padding: "6px 12px",
  fontSize: "0.82rem",
  fontFamily: "Cairo, sans-serif",
  cursor: "pointer",
  outline: "none",
};

// Gold bg + burgundy text — matches admin bg-secondary/80 text-primary
const btnPrimary = {
  background: "rgba(241,225,148,0.85)",
  border: "1px solid rgba(91,14,20,0.2)",
  color: "#5B0E14",
  borderRadius: 10,
  padding: "9px 20px",
  fontWeight: 800,
  fontSize: "0.88rem",
  fontFamily: "Cairo, sans-serif",
  cursor: "pointer",
};

const btnGhost = {
  background: "#f9fafb",
  border: "1px solid #e5e7eb",
  color: "rgba(91,14,20,0.6)",
  borderRadius: 10,
  padding: "9px 20px",
  fontWeight: 700,
  fontSize: "0.88rem",
  fontFamily: "Cairo, sans-serif",
  cursor: "pointer",
};

const formTitleStyle = {
  color: "#5B0E14",
  fontWeight: 800,
  marginBottom: 20,
  fontSize: "1.1rem",
};
